import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strict allow-list regexes for each tracking ID. Values flow from the admin
// panel via the API and are interpolated into inline <script> tags, so we
// MUST reject anything that could break out of the string context (quotes,
// backticks, angle brackets, semicolons). Anything not matching = ignored.
const ID_PATTERNS = {
  gtm: /^GTM-[A-Z0-9]{4,10}$/,
  ga4: /^G-[A-Z0-9]{6,12}$/,
  googleAds: /^AW-\d{6,12}$/,
  metaPixel: /^\d{10,20}$/,
  linkedin: /^\d{4,12}$/,
  hotjar: /^\d{4,12}$/,
};

const safeId = (value, pattern) => {
  if (typeof value !== 'string') return null;
  return pattern.test(value) ? value : null;
};

/**
 * TrackingPixels - Dynamically injects GA4, Meta Pixel, LinkedIn Insight,
 * GTM, Google Ads, and Hotjar scripts based on backend configuration.
 * All IDs are validated against strict allow-list regexes to prevent
 * XSS via inline <script> interpolation.
 */
export default function TrackingPixels() {
  const [injected, setInjected] = useState(false);

  useEffect(() => {
    if (injected) return;

    axios.get(`${API}/tracking-pixels`).then(({ data }) => {
      if (!data?.enabled) return;

      const gtm = safeId(data.gtm_id, ID_PATTERNS.gtm);
      const ga4 = safeId(data.ga4_id, ID_PATTERNS.ga4);
      const googleAds = safeId(data.google_ads_id, ID_PATTERNS.googleAds);
      const metaPixel = safeId(data.meta_pixel_id, ID_PATTERNS.metaPixel);
      const linkedin = safeId(data.linkedin_id, ID_PATTERNS.linkedin);
      const hotjar = safeId(data.hotjar_id, ID_PATTERNS.hotjar);

      // Google Tag Manager
      if (gtm) {
        const s = document.createElement('script');
        s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtm}');`;
        document.head.appendChild(s);
      }

      // Google Analytics 4
      if (ga4) {
        const g = document.createElement('script');
        g.async = true;
        g.src = `https://www.googletagmanager.com/gtag/js?id=${ga4}`;
        document.head.appendChild(g);

        const s = document.createElement('script');
        s.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}
          gtag('js',new Date());gtag('config','${ga4}');`;
        document.head.appendChild(s);
      }

      // Google Ads
      if (googleAds) {
        const s = document.createElement('script');
        s.text = `gtag('config','${googleAds}');`;
        document.head.appendChild(s);
      }

      // Meta Pixel
      if (metaPixel) {
        const s = document.createElement('script');
        s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
          n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
          (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
          fbq('init','${metaPixel}');fbq('track','PageView');`;
        document.head.appendChild(s);
      }

      // LinkedIn Insight Tag
      if (linkedin) {
        const s = document.createElement('script');
        s.text = `_linkedin_partner_id="${linkedin}";window._linkedin_data_partner_ids=
          window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          (function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};
          window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];
          var b=document.createElement("script");b.type="text/javascript";b.async=true;
          b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";
          s.parentNode.insertBefore(b,s)})(window.lintrk);`;
        document.head.appendChild(s);
      }

      // Hotjar
      if (hotjar) {
        const s = document.createElement('script');
        s.text = `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:${hotjar},hjsv:6};a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j;
          a.appendChild(r)})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=6');`;
        document.head.appendChild(s);
      }

      setInjected(true);
    }).catch(() => {});
  }, [injected]);

  return null;
}
